import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';

const BAR_COLOR = '#93b7f0';
const BAR_COLOR_SELECTED = '#1976D2';

export interface MeetingBucket {
  key: string;
  label: string; // e.g. "Next 7 days" / "Mon" / "Jul"
  subLabel?: string; // e.g. "1–7 Jul", or "2026" on the first month / a year change
  tooltipLabel?: string; // e.g. "Jul 2026" — richer label for the hover tooltip
  count: number;
}

interface HomeInsightsProps {
  buckets: MeetingBucket[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
}

// Two-line X-axis tick: bold label above, muted date range below.
const BucketTick = ({ x, y, payload, buckets }: any) => {
  const b: MeetingBucket | undefined = buckets[payload.index];
  if (!b) return null;
  return (
    <g transform={`translate(${x},${y + 12})`}>
      <text textAnchor="middle" fontSize={12} fontWeight={600} fill="#374151">
        {b.label}
      </text>
      {b.subLabel && (
        <text textAnchor="middle" fontSize={11} fill="#9ca3af" dy={16}>
          {b.subLabel}
        </text>
      )}
    </g>
  );
};

const ChartTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const b: MeetingBucket = payload[0].payload;
  return (
    <Paper elevation={3} sx={{ p: 1.25, borderRadius: '8px' }}>
      <Typography variant="body2" fontWeight={700}>
        {b.tooltipLabel || b.subLabel || b.label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {b.count} {b.count === 1 ? 'meeting' : 'meetings'}
      </Typography>
    </Paper>
  );
};

const HomeInsights: React.FC<HomeInsightsProps> = ({ buckets, selectedKey, onSelect }) => (
  // Fills its container — the caller owns the height so the chart can give up
  // space to the meetings table when the viewport is short.
  <Box id="home-insights" sx={{ width: '100%', height: '100%', minHeight: 0 }}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={buckets} margin={{ top: 24, right: 12, left: 0, bottom: 28 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#eef0f4" />
        <XAxis
          dataKey="label"
          interval={0}
          tickLine={false}
          axisLine={{ stroke: '#e5e7eb' }}
          height={44}
          tick={<BucketTick buckets={buckets} />}
        />
        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={32} />
        <Tooltip cursor={{ fill: 'rgba(25,118,210,0.06)' }} content={<ChartTooltip />} />
        <Bar
          dataKey="count"
          radius={[6, 6, 0, 0]}
          maxBarSize={72}
          cursor="pointer"
          onClick={(data: any) => data?.key && onSelect(data.key)}
        >
          <LabelList dataKey="count" position="top" style={{ fontSize: 12, fontWeight: 700, fill: '#374151' }} />
          {buckets.map((b) => (
            <Cell
              key={b.key}
              fill={selectedKey === b.key ? BAR_COLOR_SELECTED : BAR_COLOR}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </Box>
);

export default HomeInsights;
